import React from 'react';
import ConfirmationDialog from './ConfirmationDialog';

/**
 * File Delete Confirmation Dialog Component
 * Specialized confirmation dialog for Knowledge Base file deletion
 * Shows file information and parrot usage details
 */
const FileDeleteConfirmationDialog = ({
  isOpen,
  file,
  parrotUsage = [],
  onConfirm,
  onCancel,
  loading = false
}) => {
  if (!file) return null;

  const formatFileSize = (bytes) => {
    if (!bytes || bytes === 0) return 'Unknown size';
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i];
  };

  const getFileTypeIcon = (type) => {
    switch (type) {
      case 'file':
        return (
          <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
        );
      case 'text':
        return (
          <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h7" />
          </svg>
        );
      case 'url':
        return (
          <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
          </svg>
        );
      default:
        return (
          <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
        );
    }
  };

  const hasParrotUsage = parrotUsage && parrotUsage.length > 0;

  return (
    <ConfirmationDialog
      isOpen={isOpen}
      title="Delete Knowledge Base File"
      message={`Are you sure you want to delete "${file.name}"? This action cannot be undone.`}
      confirmText="Delete File"
      cancelText="Cancel"
      onConfirm={onConfirm}
      onCancel={onCancel}
      loading={loading}
      variant="danger"
    >
      {/* File Information */}
      <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 space-y-3 dark:bg-gray-900">
        <div className="flex items-center space-x-3">
          <div className="w-8 h-8 bg-red-100 dark:bg-red-900/30 rounded flex items-center justify-center flex-shrink-0">
            <div className="text-red-600 dark:text-red-400">
              {getFileTypeIcon(file.type)}
            </div>
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium text-gray-900 dark:text-white truncate" title={file.name}>
              {file.name}
            </p>
            <div className="flex items-center space-x-2 text-xs text-gray-500 dark:text-gray-400">
              <span className="capitalize">{file.type || 'File'}</span>
              {(file.size || file.formattedSize) && (
                <>
                  <span>•</span>
                  <span>{file.formattedSize || formatFileSize(file.size)}</span>
                </>
              )}
              {file.created_at && (
                <>
                  <span>•</span>
                  <span>{new Date(file.created_at).toISOString().split('T')[0]}</span>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Parrot Usage Information */}
      {hasParrotUsage && (
        <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
          <div className="flex items-start space-x-3">
            <div className="flex-shrink-0">
              <svg 
                className="h-5 w-5 text-yellow-600 dark:text-yellow-400 mt-0.5" 
                fill="currentColor" 
                viewBox="0 0 20 20"
                aria-hidden="true"
              >
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="min-w-0 flex-1">
              <h4 className="text-sm font-medium text-yellow-800 dark:text-yellow-200 mb-2">
                Parrot Usage Warning
              </h4>
              <p className="text-sm text-yellow-700 dark:text-yellow-300 mb-3">
                This file is currently used by {parrotUsage.length} parrot{parrotUsage.length !== 1 ? 's' : ''}:
              </p>
              <ul className="space-y-1">
                {parrotUsage.map((parrotName, index) => (
                  <li key={index} className="flex items-center space-x-2 text-sm text-yellow-700 dark:text-yellow-300">
                    <svg 
                      className="h-3 w-3 flex-shrink-0" 
                      fill="currentColor" 
                      viewBox="0 0 20 20"
                      aria-hidden="true"
                    >
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    <span className="truncate">{parrotName}</span>
                  </li>
                ))}
              </ul>
              <p className="text-sm text-yellow-700 dark:text-yellow-300 mt-3">
                Deleting this file will remove it from all these parrots and may affect their ability to answer questions about this content.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Impact Warning */}
      <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
        <div className="flex items-start space-x-3">
          <div className="flex-shrink-0">
            <svg 
              className="h-5 w-5 text-red-600 dark:text-red-400 mt-0.5" 
              fill="currentColor" 
              viewBox="0 0 20 20"
              aria-hidden="true"
            >
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="min-w-0 flex-1">
            <h4 className="text-sm font-medium text-red-800 dark:text-red-200 mb-1">
              Permanent Deletion
            </h4>
            <p className="text-sm text-red-700 dark:text-red-300">
              This file will be permanently removed from your knowledge base. 
              {hasParrotUsage 
                ? ' Your AI agents will no longer have access to this information and may provide less accurate responses for related questions.'
                : ' This action cannot be undone.'
              }
            </p>
          </div>
        </div>
      </div>
    </ConfirmationDialog>
  );
};

export default FileDeleteConfirmationDialog;
